// Theme-able utilities for hex tile images per domain.

export type Domain = 'deadlands' | 'drylands' | 'greenlands' | 'icelands' | 'sandlands';

// Theme interface describing how to resolve images for domains/tiles.
export interface HexTileTheme {
    id: string;
    name: string;
    // Return all (filename -> url) pairs for a given domain
    list(domain: Domain): Array<{ filename: string; url: string }>;
    // Resolve a single tile filename to a URL
    get(domain: Domain, filename: string): string | null;
}

// Helper to build a theme from Vite glob imports.
function createGlobTheme(id: string, name: string): HexTileTheme {
    // Use both relative and absolute-from-project-root globs to support different build setups.
    const rel = import.meta.glob('../../../images/{deadlands,drylands,greenlands,icelands,sandlands}/*.{png,jpg,jpeg,webp,svg}', {
        eager: true,
        as: 'url',
    }) as Record<string, string>;
    const abs = import.meta.glob('/resources/images/{deadlands,drylands,greenlands,icelands,sandlands}/*.{png,jpg,jpeg,webp,svg}', {
        eager: true,
        as: 'url',
    }) as Record<string, string>;
    const modules = { ...rel, ...abs } as Record<string, string>;

    // Build domain map for quick lookup
    const map: Record<Domain, Record<string, string>> = {
        deadlands: {},
        drylands: {},
        greenlands: {},
        icelands: {},
        sandlands: {},
    };

    Object.entries(modules).forEach(([path, url]) => {
        // path like '/resources/images/<domain>/<file>' or '../../../images/<domain>/<file>'
        const parts = path.split('/');
        const domain = parts[parts.length - 2] as Domain;
        const filename = parts[parts.length - 1];
        if (map[domain]) {
            map[domain][filename] = url as string;
        }
    });

    if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug(`[tiles:${id}] counts per domain:`, Object.fromEntries(Object.entries(map).map(([d, m]) => [d, Object.keys(m).length])));
    }

    return {
        id,
        name,
        list(domain: Domain) {
            return Object.entries(map[domain] || {}).map(([filename, url]) => ({ filename, url }));
        },
        get(domain: Domain, filename: string) {
            return map[domain]?.[filename] ?? null;
        },
    };
}

// Theme registry. Start with a single "default" theme mapping to current assets.
const themes: Record<string, HexTileTheme> = {
    default: createGlobTheme('default', 'Default'),
};

// Theme selection. For now, always use "default"; future: read from settings/localStorage.
function getCurrentTheme(): HexTileTheme {
    // Example future-proofing: const id = localStorage.getItem('sellune.mapTileTheme') || 'default';
    const id = 'default';
    return themes[id] ?? themes.default;
}

// Public API (keeps existing signatures so the rest of the app remains unchanged)
export function listTilesByDomain(domain: Domain): Array<{ filename: string; url: string }> {
    return getCurrentTheme().list(domain);
}

export function getTileUrl(domain: Domain, filename: string): string | null {
    return getCurrentTheme().get(domain, filename);
}
